import moment, { type Moment } from "moment"
import { CUSTOM_PERIOD, getPeriodLabel, ONE_DAY, SAME_DAY, type Period } from "helpers/dates"
import { formatFullCurrency, signedPercentageFormatter } from "helpers/formatters/numbers"
import { formatChipMoment, type IntradayReportedPnl } from "./intraday-toolbar-times"

const PRICE_DELAY_MINUTES = 15
const PRICES_UNAVAILABLE_AFTER_MINUTES = 20

export type ReportedPnlState = "pending" | "applied" | "pricesUnavailable"

export interface ReportedPnlLine {
  symbol: "○" | "●"
  text: string
  description: string
}

export function getReportedPnlState(reportedPnl: IntradayReportedPnl, now: Moment): ReportedPnlState {
  if (reportedPnl.isApplied === true) return "applied"
  const unavailableAt = moment(reportedPnl.asOf, moment.ISO_8601).add(PRICES_UNAVAILABLE_AFTER_MINUTES, "minutes")
  return now.isBefore(unavailableAt) ? "pending" : "pricesUnavailable"
}

function getReportedPnlValue({ totalReturn, amount }: IntradayReportedPnl, currency: string | null): string | null {
  const parts = [
    totalReturn != null ? signedPercentageFormatter(totalReturn) : null,
    amount != null ? `(${formatFullCurrency(amount, currency)})` : null,
  ].filter((part): part is string => part != null)
  return parts.length > 0 ? parts.join(" ") : null
}

export function getReportedPnlLine(reportedPnl: IntradayReportedPnl | null, currency: string | null, now: Moment): ReportedPnlLine | null {
  if (!reportedPnl?.asOf) return null
  const asOf = moment(reportedPnl.asOf, moment.ISO_8601)
  const time = formatChipMoment(asOf, "LT", now)
  const value = getReportedPnlValue(reportedPnl, currency)
  const text = ["Your P&L", time, value].filter((part): part is string => part != null).join(" · ")
  const state = getReportedPnlState(reportedPnl, now)

  if (state === "applied") {
    return { symbol: "●", text, description: `Included in performance. Total Return matched it at ${time}.` }
  }
  if (state === "pricesUnavailable") {
    return { symbol: "○", text, description: "prices unavailable" }
  }
  const expectedAt = formatChipMoment(asOf.clone().add(PRICE_DELAY_MINUTES, "minutes"), "LT", now)
  return { symbol: "○", text, description: `As reported. Included in performance when prices reach ${time} (about ${expectedAt}).` }
}

export interface ReportedPnlQualifierContext {
  activeBaseName: string | null
  isFxHedged: boolean
  period: Period
  basis: string | null
  comparisonMainInstrumentName: string | null
}

function getQualifierPeriodLabel(period: Period): string {
  if (period === CUSTOM_PERIOD) return "selected period"
  if (period === ONE_DAY) return "1 Day"
  return getPeriodLabel(period)
}

export function getReportedPnlQualifiers({ activeBaseName, isFxHedged, period, basis, comparisonMainInstrumentName }: ReportedPnlQualifierContext): string[] {
  const qualifiers = [
    activeBaseName != null && `Whole portfolio, absolute. Not relative to ${activeBaseName}.`,
    isFxHedged && "As reported, not hedged. With hedging on, the currency difference shows in Trading.",
    period !== SAME_DAY && `Today's P&L. Included in your ${getQualifierPeriodLabel(period)} Total Return, compounded with earlier days.`,
    basis === "GMV" && "% of total GMV, including unmodelled names.",
    comparisonMainInstrumentName != null && `For ${comparisonMainInstrumentName} only.`,
  ]
  return qualifiers.filter((qualifier): qualifier is string => typeof qualifier === "string")
}
